import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
    addUserToGroups,
    removeUserFromGroups,
    getUser,
} from "@/services/userService";
import { toast } from "sonner";

interface UserGroupsFormProps {
    username: string;
    onSuccess?: () => void;
}

// Available groups in the system
const AVAILABLE_GROUPS = ["wheel", "accounting"];

export function UserGroupsForm({ username, onSuccess }: UserGroupsFormProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userGroups, setUserGroups] = useState<string[]>([]);
    const [originalGroups, setOriginalGroups] = useState<string[]>([]);

    useEffect(() => {
        const loadUser = async () => {
            try {
                setIsLoading(true);
                const user = await getUser(username);
                setUserGroups(user.groups || []);
                setOriginalGroups(user.groups || []);
            } catch (error) {
                toast.error(
                    `Failed to load user groups: ${(error as Error).message}`
                );
            } finally {
                setIsLoading(false);
            }
        };

        loadUser();
    }, [username]);

    const handleGroupToggle = (group: string, checked: boolean) => {
        if (checked) {
            setUserGroups((prev) => [...prev, group]);
        } else {
            setUserGroups((prev) => prev.filter((g) => g !== group));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Determine which groups to add and which to remove
            const groupsToAdd = userGroups.filter(
                (group) => !originalGroups.includes(group)
            );
            const groupsToRemove = originalGroups.filter(
                (group) => !userGroups.includes(group)
            );

            // Update groups if there are changes
            if (groupsToAdd.length > 0) {
                await addUserToGroups(username, groupsToAdd);
            }

            if (groupsToRemove.length > 0) {
                await removeUserFromGroups(username, groupsToRemove);
            }

            // Update original groups to reflect the current state
            setOriginalGroups([...userGroups]);

            toast.success("User groups updated successfully");

            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            toast.error(`Failed to update groups: ${(error as Error).message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <div>Loading user groups...</div>;
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label>User Groups</Label>
                <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_GROUPS.map((group) => (
                        <div
                            key={group}
                            className="flex items-center space-x-2"
                        >
                            <Checkbox
                                id={`group-${group}`}
                                checked={userGroups.includes(group)}
                                onCheckedChange={(checked) =>
                                    handleGroupToggle(group, checked === true)
                                }
                            />
                            <Label htmlFor={`group-${group}`}>{group}</Label>
                        </div>
                    ))}
                </div>
            </div>

            <Button
                type="submit"
                disabled={
                    isSubmitting ||
                    JSON.stringify(userGroups) ===
                        JSON.stringify(originalGroups)
                }
            >
                {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
        </form>
    );
}
